import {DefaultFeedSource} from "../feedSource";
import {customElement, property, queryAll} from "lit/decorators.js";
import {AbstractFeedPreviewGrid, AbstractFeedPreviewSlider} from "./feedPreviewCommons";
import {EventBus} from "../../../common/eventBus";
import {resolve} from "../../../container";
import {html, type TemplateResult} from "lit";
import {MultiScrollCarouselLayout} from "../../../page/elements/multiScrollCarousel";
import type {Nullable} from "../../../bootstrap/common/types";
import {EopYoutubeShortTile, YOUTUBE_SHORT_TILE_PLAY_VIDEO_EVENT} from "../../../page/elements/youtubeShortTile";
import {MultiRowFeedPreviewNarrowSize} from "./feedPreviewSize";

@customElement("eop-feed-preview")
export class EopFeedPreview extends AbstractFeedPreviewGrid<DefaultFeedSource> {

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "sources"})
    public sources: string;
    @property({attribute: "keywords"})
    public keywords: string;
    @property({attribute: "excluded-keywords"})
    public excludedKeywords: string;
    @property({attribute: "show-more", type: Boolean})
    public enableMore: boolean = false;

    public constructor(feedSource: DefaultFeedSource = new DefaultFeedSource()) {
        super(feedSource);
    }

    protected configure(): void {
        this.feedSource.configure({
            sources: this.sources?.parseAsJSON() ?? {},
            keywords: this.keywords?.parseCommaSeparated() ?? [],
            excludedKeywords: this.excludedKeywords?.parseCommaSeparated() ?? [],
            characterLimit: this.characterLimit,
            language: this.language
        });
    }
}

@customElement("eop-feed-preview-slider")
export class EopFeedPreviewSlider extends AbstractFeedPreviewSlider<DefaultFeedSource> {

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "sources"})
    public sources: string;
    @property({attribute: "keywords"})
    public keywords: string;
    @property({attribute: "excluded-keywords"})
    public excludedKeywords: string;

    public constructor(feedSource: DefaultFeedSource = new DefaultFeedSource()) {
        super(feedSource);
    }

    protected configure(): void {
        this.feedSource.configure({
            sources: this.sources?.parseAsJSON() ?? {},
            keywords: this.keywords?.parseCommaSeparated() ?? [],
            excludedKeywords: this.excludedKeywords?.parseCommaSeparated() ?? [],
            characterLimit: this.characterLimit,
            language: this.language
        });
    }
}

@customElement("eop-feed-video-slider")
export class EopFeedVideoSlider extends AbstractFeedPreviewSlider<DefaultFeedSource> {

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "sources"})
    public sources: string;
    @property({attribute: "keywords"})
    public keywords: string;
    @property({attribute: "excluded-keywords"})
    public excludedKeywords: string;
    @queryAll("eop-youtube-short-tile")
    private videoTiles: EopYoutubeShortTile[];

    public constructor(feedSource: DefaultFeedSource = new DefaultFeedSource(), private eventBus: EventBus = resolve(EventBus)) {
        super(feedSource);
    }

    protected configure(): void {
        this.feedSource.configure({
            sources: this.sources?.parseAsJSON() ?? {},
            keywords: this.keywords?.parseCommaSeparated() ?? [],
            excludedKeywords: this.excludedKeywords?.parseCommaSeparated() ?? [],
            characterLimit: this.characterLimit,
            language: this.language
        });
    }

    protected setTileAmount(): void {
        this.limit = new MultiRowFeedPreviewNarrowSize(this.size, this.resolution).getTileAmount();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.eventBus.on(YOUTUBE_SHORT_TILE_PLAY_VIDEO_EVENT, (_event) => {
            this.videoTiles.forEach(tile => tile.pauseVideo());
        });
    }

    protected renderMultiCarousel(): Nullable<TemplateResult> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }
        return html`
            <eop-multi-carousel .layout=${MultiScrollCarouselLayout.NARROW_TILES}>
                ${this.renderTiles()}
            </eop-multi-carousel>
        `;
    }

    protected renderTiles(): Nullable<TemplateResult> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }

        const feedEntries = this.feedEntries.map(entry => html`
            <eop-youtube-short-tile
                    slot="slider-content"
                    .headline=${entry.data.headline}
                    .subHeadline=${entry.data.subHeadline}
                    .videoId=${EopYoutubeShortTile.extractYoutubeVideoIdFromUrl(entry.data.url)}
                    .alt=${entry.data.imageAltText}
            ></eop-youtube-short-tile>`);
        return html`${feedEntries}`;
    }
}